import { signal } from '@angular/core';

import type {
  FinAccount,
  FinBillable,
  FinContact,
  FinDataset,
  FinDocument,
  FinExpense,
  FinProject,
  FinQuote,
  FinSettings,
} from 'src/app/pages/finances/data/finances.model';
import { FIN_DEMO_DATASET } from './finances-demo-data';
import { sampleApplyCredit, sampleCancel, sampleCorrect, sampleKeepCredit, sampleRefund } from './finances-demo-corrections';

/**
 * Finances sample layer — answers the `/finances/{org}/…` endpoints `backend/api` does not have yet, with the
 * mockup's corrected dataset (Accent Network, "today" 26 May 2026).
 *
 * Its own switch, deliberately independent of `INVENTED_DATA_ENABLED` (Dani, 1 Oct: real data only): Finances was
 * always a sample-data page, admin only and tagged PLANNED, and Ansar (16 Oct) asked for the redesign on sample data
 * with real requests until David builds the backend from `.claude-docs/finances-redesign-API-CONTRACT.md`. Every
 * answer it gives is recorded in {@link financesSampleUsed}, which the page's "Sample data" mark reads.
 *
 * Actions (reminders, payments, approvals, renewals…) answer "done" without changing the documents, exactly as the
 * mockup does: the page marks the item done for the visit. The real backend will change the documents instead.
 * Corrective invoices (README WP9) are the exception: correcting, cancelling a sent invoice, taking credit off,
 * refunding and keeping credit change the visit's documents (`finances-demo-corrections.ts`), as the mockup does, so
 * the page can follow Dan's five steps.
 *
 * **To remove it:** delete the `finances-demo-*` files and the word `financesDemoBackendInterceptor` from
 * `withInterceptors([...])` in `app.config.ts`.
 */
export const FINANCES_DEMO_ENABLED = true;

/** What the sample layer answered, by endpoint family; the page's mark lists them. */
export type FinancesSampleSource =
  | 'settings' | 'accounts' | 'contacts' | 'projects' | 'documents' | 'quotes' | 'billables' | 'expenses' | 'actions' | 'tax';

export const FINANCES_SAMPLE_LABELS: Record<FinancesSampleSource, string> = {
  settings: 'currencies and today’s date',
  accounts: 'bank accounts',
  contacts: 'clients and vendors',
  projects: 'projects',
  documents: 'invoices and bills',
  quotes: 'quotes',
  billables: 'work not yet invoiced',
  expenses: 'expenses',
  actions: 'actions',
  tax: 'tax defaults',
};

const used = signal<ReadonlySet<FinancesSampleSource>>(new Set());

/** Sources answered from samples so far; empty once the server answers everything. */
export const financesSampleUsed = used.asReadonly();

export function noteFinancesSample(source: FinancesSampleSource): void {
  if (used().has(source)) return;
  used.set(new Set([...used(), source]));
}

// ── The session's copy of the dataset ──────────────────────────────────────────────────────────

let data: FinDataset = structuredClone(FIN_DEMO_DATASET);

/** Back to the untouched dataset (specs). */
export function resetFinancesSample(): void {
  data = structuredClone(FIN_DEMO_DATASET);
  used.set(new Set());
}

/** Every answer is a copy, so the page can never change the session's data by accident. */
const copy = <T>(v: T): T => structuredClone(v);

export function sampleSettings(): FinSettings {
  return copy({ asOf: data.today, organization: data.org, fx: data.fx, services: data.services, langs: data.langs });
}

export const sampleAccounts = (): FinAccount[] => copy(data.accounts);
export const sampleContacts = (): FinContact[] => copy(data.contacts);
export const sampleProjects = (): FinProject[] => copy(data.projects);
export const sampleDocuments = (): FinDocument[] => copy(data.documents);
export const sampleQuotes = (): FinQuote[] => copy(data.quotes);
export const sampleBillables = (): FinBillable[] => copy(data.billables);
export const sampleExpenses = (): FinExpense[] => copy(data.expenses);

/**
 * The default tax for a draft (`POST tax/suggest`), worked out by Dani's rules from the sample organization's and the
 * client's countries. The rules and their 161 territories load only when asked (a separate chunk).
 */
export async function sampleTax(body: unknown): Promise<unknown> {
  const { sampleTaxSuggestion } = await import('./finances-demo-tax');
  return sampleTaxSuggestion(body as { clientId?: string; date?: string; lang?: string } | null, data.org, data.contacts, data.today);
}

/** Corrective invoices (README WP9): these change the visit's documents and answer the one the server would. */
export const sampleCorrection = (id: string, body: unknown) => copy(sampleCorrect(data, id, body));
export const sampleVoid = (id: string) => copy(sampleCancel(data, id));
export const sampleCredit = (id: string, body: unknown) => copy(sampleApplyCredit(data, id, body));
export const sampleRefundOf = (id: string, body: unknown) => copy(sampleRefund(data, id, body));
export const sampleKeep = (id: string) => copy(sampleKeepCredit(data, id));

/** An action on a document, quote, piece of work or expense: accepted, nothing changes (see the header). */
export function sampleAction(): { ok: true } {
  return { ok: true };
}
